import { type UseBaseQueryOptions, type UseQueryOptions, useQueries, type QueryObserverResult } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import { QueryKeys } from 'utils/queryKeys';
import { capitalizeFirstLetter } from 'utils/formatStringHandlers';
import { trackError } from '@proposify/frontend-observability';
import { customFieldsQueries } from 'services/custom-fields/customFieldsQueries';
import { mapAssignedCustomFieldsToVariableNodes, type AssignedCustomField } from 'services/custom-fields/schemas';
import { openNotification } from 'components/notification';
import i18next from 'i18next';
import {
  type VariableEntity,
  type VariablesGroup,
  type VariableNode,
  makeRootVariableNode,
  VariableDomain,
  makeFolderNode,
} from 'services/entities/VariablesEntity';
import { ContentType } from 'utils/getContentType';

export type VariablesMode = ContentType | 'preview';

const DOMAIN_LABEL: Record<string, string> = {
  proposal: VariableDomain.DOCUMENT,
  client: VariableDomain.CLIENT,
  user: VariableDomain.USER,
};

export const sanitizePathSegment = (segment: string) => segment.replaceAll('/', '-');

function mapDefaultVariablesToLeaves(variables: VariablesGroup | null): VariableNode[] {
  if (variables === null) return [];

  return Object.entries(variables).flatMap(([domain, entities]: [string, VariableEntity[]]) => {
    const domainLabel = DOMAIN_LABEL[domain];
    if (!domainLabel) {
      trackError(new Error(`Unknown variable domain: "${domain}"`), { errorFrom: 'editor-remote', feature: 'variables' });
      return [];
    }
    return entities.map((e) => ({
      ...e,
      name: e.name ?? e.friendlyName,
      variables: [],
      path: `All/${domainLabel}/${sanitizePathSegment(capitalizeFirstLetter(e.friendlyName))}`,
    }));
  });
}

export function defaultVariablesQueryOptions({
  contentId,
  mode,
  v2RouteEnabled = false,
  v2RouteFlagLoading = false,
}: {
  contentId: string;
  mode?: VariablesMode;
  v2RouteEnabled?: boolean;
  v2RouteFlagLoading?: boolean;
}): UseBaseQueryOptions<VariablesGroup, Error, VariableNode[]> {
  return {
    queryKey: [...QueryKeys.variables, contentId, ...(v2RouteEnabled ? ['v2'] : [])],
    queryFn: () => apiDocumentServiceRepository.getVariables(contentId, v2RouteEnabled),
    staleTime: Infinity,
    enabled: mode !== ContentType.SECTION && !!contentId && !v2RouteFlagLoading,
    select: mapDefaultVariablesToLeaves,
  };
}

function crmVariablesQueryOptions({
  contentId,
  mode,
}: {
  contentId: string;
  mode: VariablesMode;
}): UseQueryOptions<VariableNode[], Error, VariableNode[], readonly unknown[]> {
  return {
    staleTime: Infinity,
    enabled: mode !== ContentType.SECTION && !!contentId,
    queryKey: [...QueryKeys.crmVariables, contentId],
    queryFn: () =>
      mode === ContentType.TEMPLATE
        ? apiDocumentServiceRepository.getTemplateCRMVariables(contentId)
        : apiDocumentServiceRepository.getCRMVariables(contentId),
  };
}

// Module-level, not inline arrows: `combine` compares `r.data` by reference; a fresh
// select identity per render defeats query memoization, breaking the dataUnchanged check.
const selectCustomFieldNodesWithShape = (fields: AssignedCustomField[]) => mapAssignedCustomFieldsToVariableNodes(fields, true);
const selectCustomFieldNodes = (fields: AssignedCustomField[]) => mapAssignedCustomFieldsToVariableNodes(fields, false);

function customVariablesQueryOptions(
  contentId: string,
  mode: VariablesMode,
  shapeEnabled: boolean
): UseQueryOptions<AssignedCustomField[], Error, VariableNode[]> {
  const { queryKey, queryFn } = customFieldsQueries.listEditorAssignedCustomFields(contentId, mode === ContentType.TEMPLATE);
  return {
    queryKey,
    queryFn,
    select: shapeEnabled ? selectCustomFieldNodesWithShape : selectCustomFieldNodes,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnMount: 'always',
    enabled: mode !== ContentType.SECTION && !!contentId,
  };
}

function buildPathTree(leaves: VariableNode[]): VariableNode[] {
  const root: VariableNode = { ...makeRootVariableNode(), variables: [] };

  for (const leaf of leaves) {
    const segments = leaf.path.split('/').filter(Boolean);
    const folderSegments = segments.slice(0, -1);

    let current = root;
    for (let i = 0; i < folderSegments.length; i++) {
      const folderPath = folderSegments.slice(0, i + 1).join('/');
      if (folderPath === 'All') continue;

      let folder = current.variables!.find((v) => v.path === folderPath);
      if (!folder) {
        folder = makeFolderNode({ name: folderSegments[i], friendlyName: folderSegments[i], path: folderPath, variables: [] });
        current.variables!.push(folder);
      }
      current = folder;
    }

    current.variables!.push(leaf);
  }

  return root.variables!;
}

function notifyWhenError(results: Array<QueryObserverResult<VariableNode[], Error>>, mode: VariablesMode) {
  if (results.some((r) => r.isError)) {
    openNotification({
      type: 'error',
      title: i18next.t('editor.variables.query_error'),
      customDescription:
        mode === 'preview'
          ? i18next.t('editor.variables.query_error_description_preview')
          : `${i18next.t('editor.variables.query_error_description_1')}<a href="mailto:support@proposify.com">${i18next.t(
              'editor.variables.query_error_description_2'
            )}</a>.`,
      action: {
        label: i18next.t('editor.variables.query_error_refresh', { defaultValue: 'Refresh page' }),
        onClick: () => window.location.reload(),
      },
    });
  }
}

export function combineVariablesQueries(results: Array<QueryObserverResult<VariableNode[], Error>>): VariableNode {
  const [crm = [], custom = [], defaults = []] = results.map((r) => r.data ?? []);
  const pathNodes = buildPathTree([...defaults, ...custom, ...crm]);

  return { ...makeRootVariableNode(), variables: pathNodes };
}

export const BATCH_RENDER_THRESHOLD_MS = 500;

export interface UseCombinedVariablesQueriesOptions {
  customFieldShapeEnabled?: boolean;
  appNativeVariablesV2Route?: { enabled: boolean; isLoading: boolean };
}

const DEFAULT_APP_NATIVE_VARIABLES_V2_ROUTE = { enabled: false, isLoading: false };

const isSettled = (r: QueryObserverResult<VariableNode[], Error>) => !r.isPending || r.fetchStatus === 'idle';

export type CombinedVariablesErrors = {
  crmError: boolean;
  customFieldsError: boolean;
  defaultsError: boolean;
};

export type CombinedVariablesQueryResult = {
  data: VariableNode;
  errors: CombinedVariablesErrors;
};

export function useCombinedVariablesQueries(
  contentId: string,
  mode: VariablesMode,
  {
    customFieldShapeEnabled = false,
    appNativeVariablesV2Route = DEFAULT_APP_NATIVE_VARIABLES_V2_ROUTE,
  }: UseCombinedVariablesQueriesOptions = {}
): CombinedVariablesQueryResult {
  const mountTimeRef = useRef(Date.now());
  const latestResultRef = useRef<VariableNode>({ ...makeRootVariableNode(), variables: [] });
  const latestDataRef = useRef<Array<VariableNode[] | undefined>>([]);
  const latestErrorsRef = useRef<CombinedVariablesErrors>({ crmError: false, customFieldsError: false, defaultsError: false });

  const combine = useCallback(
    (results: Array<QueryObserverResult<VariableNode[], Error>>): CombinedVariablesQueryResult => {
      notifyWhenError(results, mode);
      const allSettled = results.every(isSettled);

      const withinBatchWindow = Date.now() - mountTimeRef.current < BATCH_RENDER_THRESHOLD_MS;

      if (withinBatchWindow && !allSettled) {
        return { data: latestResultRef.current, errors: latestErrorsRef.current };
      }

      const currentData = results.map((r) => r.data);
      const dataUnchanged =
        currentData.length === latestDataRef.current.length && currentData.every((data, i) => data === latestDataRef.current[i]);

      if (dataUnchanged) {
        return { data: latestResultRef.current, errors: latestErrorsRef.current };
      }

      latestDataRef.current = currentData;
      latestResultRef.current = combineVariablesQueries(results);
      latestErrorsRef.current = {
        crmError: results[0].isError,
        customFieldsError: results[1].isError,
        defaultsError: results[2].isError,
      };
      return { data: latestResultRef.current, errors: latestErrorsRef.current };
    },
    [mode]
  );

  return useQueries({
    queries: [
      crmVariablesQueryOptions({
        contentId,
        mode,
      }),
      customVariablesQueryOptions(contentId, mode, customFieldShapeEnabled),
      defaultVariablesQueryOptions({
        contentId,
        mode,
        v2RouteEnabled: appNativeVariablesV2Route.enabled,
        v2RouteFlagLoading: appNativeVariablesV2Route.isLoading,
      }),
    ],
    combine,
  });
}
